import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";

/**
 * Public claim landing: /claim?slug=<>&token=<>
 *
 * - If the visitor isn't signed in as a dispensary owner, route them through
 *   register/login first (preserving the claim params).
 * - On submit, POST /api/dispensary/claim — server verifies the token and adds
 *   the shop to the user's portfolio.
 */
export default function ClaimPage() {
  const [params] = useSearchParams();
  const slug = params.get("slug") || "";
  const token = params.get("token") || "";
  const { user, authChecked } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug || !token) setError("Missing claim link parameters");
  }, [slug, token]);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/dispensary/claim", {
        slug,
        claim_token: token,
      });
      setDone(r.data);
      toast.success("Claimed! Redirecting to dashboard…");
      setTimeout(() => navigate("/dispensary/dashboard"), 1500);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!authChecked)
    return (
      <div className="container-narrow py-20 text-center">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );

  if (!user) {
    return (
      <div className="container-narrow py-12">
        <div className="max-w-md mx-auto border border-border bg-card rounded-2xl p-8 text-center">
          <KeyRound className="w-7 h-7 mx-auto text-accent mb-4" />
          <div className="eyebrow mb-1">Claim your dispensary</div>
          <h1 className="font-serif text-3xl mb-3">Sign in to continue</h1>
          <p className="text-sm text-foreground/60 mb-6">
            You need a dispensary owner account to take over this listing.
            We'll bring you right back to finish the claim.
          </p>
          <div className="space-y-3">
            <Link
              data-testid="claim-signin-link"
              to={`/dispensary/login?next=/claim?slug=${slug}&token=${token}`}
              className="block pill pill-primary"
            >
              I already have an account
            </Link>
            <Link
              data-testid="claim-signup-link"
              to={`/dispensary/signup?next=/claim?slug=${slug}&token=${token}`}
              className="block pill"
            >
              Create a new account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-narrow py-12">
      <div
        className="max-w-md mx-auto border border-border bg-card rounded-2xl p-8 text-center"
        data-testid="claim-card"
      >
        {done ? (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-4" />
            <h1 className="font-serif text-3xl mb-2">You're in.</h1>
            <p className="text-sm text-foreground/60">
              <strong>{done.name}</strong> is now under your account. Loading
              the dashboard…
            </p>
          </>
        ) : error ? (
          <>
            <AlertCircle className="w-8 h-8 mx-auto text-rose-300 mb-4" />
            <h1 className="font-serif text-2xl mb-2">Couldn't claim</h1>
            <p className="text-sm text-foreground/60 mb-5">{error}</p>
            <p className="text-xs text-muted-foreground">
              If the link expired, ask the Bud Link team to send a fresh one.
            </p>
          </>
        ) : (
          <>
            <KeyRound className="w-8 h-8 mx-auto text-accent mb-4" />
            <div className="eyebrow mb-1">Pre-onboarded shop</div>
            <h1 className="font-serif text-3xl mb-2">Take ownership</h1>
            <p className="text-sm text-foreground/60 mb-3">
              Bud Link set up a listing for{" "}
              <code className="text-foreground/80">{slug}</code> on your
              behalf. Claim it to start managing your menu, deals, and
              integration.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              You'll be signed in as{" "}
              <span className="text-foreground/80">{user.email}</span>.
            </p>
            <Button
              data-testid="claim-submit"
              onClick={accept}
              disabled={busy}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Claim this dispensary"
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
